// #FIELD_SLIM_LISTS_V1 (2026-10-20) — field dashboards never load the HR file (wages) or the fleet master (costs).
// Whenever either is saved, App.js `ss` also writes a slim copy with only what a field screen needs:
//   field-roster    ← hr-employees     active hourly people: hrid, name, role, email
//   fleet-unit-list ← fleet-equipment  active units: unit, desc, cat
//   revenue-lf      ← revenue-input    billed footage by supervisor + pull date (no invoice dollars)   #FIELD_SLIM_LISTS_V2
//   ru-rigs         ← ru-<year>        who is on which rig in the latest RU week (no RU revenue)
export const FIELD_ROSTER_KEY = 'field-roster';
export const UNIT_LIST_KEY = 'fleet-unit-list';
export const REVENUE_LF_KEY = 'revenue-lf';
export const RU_RIGS_KEY = 'ru-rigs';

export const slimRevenueLf = (lines) => Array.isArray(lines)
  ? lines.filter(l => l && l.sup && l.pullDate).map(l => ({ sup: l.sup, lf: l.lf == null ? '' : l.lf, pullDate: l.pullDate })) : null;
// #SUP_STANDING_V1 (2026-10-27) — Bill: footage alone misrepresents a SUP (bore size, rock); rank on revenue but never show it.
// revenue-rank holds, per quarter, each supervisor's billed work as an INDEX (100 = company average supervisor that quarter),
// keyed by pull date. No dollars, no totals — the index can't be turned back into money without the company total.
export const REVENUE_RANK_KEY = 'revenue-rank';
const _revAmt = (v) => { const n = Number(String(v == null ? '' : v).replace(/[^0-9.-]/g, '')); return isNaN(n) ? 0 : n; };
export const slimRevenueRank = (lines) => {
  if (!Array.isArray(lines)) return null;
  const tot = {};
  lines.forEach(l => {
    if (!l || !l.sup || !l.pullDate) return;
    const m = String(l.pullDate).match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/); if (!m) return;
    const y = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]); const qk = `${y}Q${Math.floor((Number(m[1]) - 1) / 3) + 1}`;
    const s = String(l.sup).toUpperCase().replace(/\./g, ' ').replace(/\s+/g, ' ').trim(); if (!s) return;
    (tot[qk] = tot[qk] || {})[s] = (tot[qk][s] || 0) + _revAmt(l.invAmnt);
  });
  const q = {};
  Object.entries(tot).forEach(([qk, by]) => {
    const vals = Object.values(by).filter(v => v > 0); const mean = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    if (!mean) return; q[qk] = {}; Object.entries(by).forEach(([s, v]) => { q[qk][s] = Math.round(Math.max(0, v) / mean * 1000) / 10; });
  });
  return { at: new Date().toISOString(), q };
};
export const slimRuRigs = (ru) => {
  if (!ru || !ru.weeks || typeof ru.weeks !== 'object') return null;
  const k = Object.keys(ru.weeks).sort().pop(); if (!k) return null; const w = ru.weeks[k] || {};
  return { year: ru.year || null, week: k, rows: (w.rows || []).filter(r => r && r.sup).map(r => ({ sup: r.sup, rig: r.rig || '', unit: r.unit || '', pm: r.pm || '' })) };
};

export const slimRoster = (emps) => (Array.isArray(emps) ? emps : [])
  .filter(x => x && x.status === 'Active' && x.payType === 'Hourly')
  .map(x => ({ hrid: x.hrid, name: `${x.first || ''} ${x.last || ''}`.replace(/\s+/g, ' ').trim(), last: x.last || '', first: x.first || '',
    nick: String(x.nickname || '').trim(),   // #TOOLBOX_TALK_V3 — crew add searches nicknames
    // #FIELD_SLIM_LISTS_V3 — company emails only (used to recognize who's signed in); personal emails stay in HR
    role: x.role || '', email: /@dakotautility\.com$/i.test(String(x.email || '').trim()) ? String(x.email).trim().toLowerCase() : '' }))
  .sort((a, b) => a.last.localeCompare(b.last) || a.first.localeCompare(b.first));

export const slimUnits = (blob) => {
  if (!blob || !Array.isArray(blob.cols)) return null;
  const iu = blob.cols.indexOf('unit'), id = blob.cols.indexOf('desc'), ic = blob.cols.indexOf('cat');
  if (iu < 0) return null;
  return (blob.active || []).filter(a => Array.isArray(a) && a[iu])
    .map(a => ({ unit: a[iu], desc: id >= 0 ? (a[id] || '') : '', cat: ic >= 0 ? (a[ic] || '') : '' }));
};

// [slimKey, slimValue] for a key that has a slim copy, else null.
export const slimFor = (k, v) => {
  if (k === 'hr-employees') return [FIELD_ROSTER_KEY, slimRoster(v)];
  if (k === 'fleet-equipment') { const u = slimUnits(v); return u ? [UNIT_LIST_KEY, u] : null; }
  if (k === 'revenue-input') { const r = slimRevenueLf(v); return r ? [REVENUE_LF_KEY, r] : null; }
  if (/^ru-\d{4}$/.test(k) && Number(k.slice(3)) === new Date().getFullYear()) { const r = slimRuRigs(v); return r ? [RU_RIGS_KEY, r] : null; }
  return null;
};
